import React from "react";
import { Button, Spinner } from "react-bootstrap";

// Rows come from the backend (OrderFinancialService::present). This component
// only displays them; it never calculates money itself.

export const formatMoney = (value, currency) => {
    if (value === null || value === undefined || value === "") return "—";
    const number = Number(value);
    if (Number.isNaN(number)) return "—";
    const text = Math.abs(number).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    const sign = number < 0 ? "−" : "";
    return currency === "USD" ? `${sign}$${text}` : `${sign}Rs ${text}`;
};

// Rows taken away from the line above them are shown with a minus.
const DEDUCTIONS = ["buying_cost", "shipping", "store_seller_commission", "mplacer_commission"];

export default function OrderFinancialSummary({ summary, onRefreshRates, refreshing, unsaved = false, updating = false }) {
    if (!summary) {
        return (
            <section className="wo-panel">
                <header className="wo-panel__head">
                    <div>
                        <h3 className="wo-panel__title">Profit summary</h3>
                        <p className="wo-panel__hint">Enter the exchange rate and buying prices to see the profit.</p>
                    </div>
                </header>
            </section>
        );
    }

    const complete = summary.status === "complete";
    const warnings = summary.warnings || [];
    const rows = (summary.rows || []).filter((row) => row.key !== "admin_profit");
    const profit = (summary.rows || []).find((row) => row.key === "admin_profit");
    const profitNumber = Number(profit?.pkr);
    const profitState = !complete || profit?.pkr === null || profit?.pkr === undefined
        ? "pending"
        : profitNumber < 0
            ? "loss"
            : "gain";

    return (
        <section className="wo-panel">
            <header className="wo-panel__head">
                <div>
                    <div className="d-flex align-items-center gap-2">
                        <h3 className="wo-panel__title">Profit summary</h3>
                        <span className={`wo-status ${complete ? "" : "wo-status--incomplete"}`}>
                            {complete ? "Complete" : "Details missing"}
                        </span>
                        {unsaved && (
                            <span className="wo-status wo-status--unsaved" title="Updated from the values you typed. Press Save to keep them.">
                                Not saved yet
                            </span>
                        )}
                        {updating && <Spinner size="sm" animation="border" className="wo-live-spinner" aria-label="Updating" />}
                    </div>
                    <p className="wo-panel__hint">
                        {summary.exchange_rate
                            ? `$1 = Rs ${summary.exchange_rate}`
                            : "Enter the exchange rate to see amounts in rupees and dollars"}
                    </p>
                </div>
                {onRefreshRates && (
                    <Button
                        size="sm"
                        className="wo-btn-quiet"
                        onClick={onRefreshRates}
                        disabled={refreshing}
                        title="Recalculate using the commission % currently set on the MPlacer and store seller profiles"
                    >
                        {refreshing ? <Spinner size="sm" animation="border" /> : "Update commission rates"}
                    </Button>
                )}
            </header>

            <div className="wo-panel__body">
                {warnings.length > 0 && (
                    <div className={`wo-notice ${complete ? "wo-notice--info" : ""}`} role="status">
                        <ul>
                            {warnings.map((warning, index) => (
                                <li key={`${warning.code}-${index}`}>{warning.message}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div style={{ overflowX: "auto" }}>
                    <table className="wo-ledger">
                        <thead>
                            <tr>
                                <th scope="col">Details</th>
                                <th scope="col">PKR</th>
                                <th scope="col">USD</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const deduct = DEDUCTIONS.includes(row.key);
                                const subtotal = row.key === "gross_profit";
                                const negative = Number(row.pkr) < 0;
                                const amountClass = subtotal && row.pkr !== null
                                    ? (negative ? "text-danger" : "text-success")
                                    : "";

                                return (
                                    <tr
                                        key={row.key}
                                        className={subtotal ? "wo-ledger--subtotal" : deduct ? "wo-ledger--deduct" : ""}
                                    >
                                        <td>
                                            {deduct ? `− ${row.label}` : row.label}
                                            {row.note && <span className="wo-ledger__note">{row.note}</span>}
                                        </td>
                                        <td className={`wo-ledger__amt ${amountClass}`}>{formatMoney(row.pkr, "PKR")}</td>
                                        <td className={`wo-ledger__amt ${amountClass}`}>{formatMoney(row.usd, "USD")}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className={`wo-profit ${profitState === "loss" ? "wo-profit--loss" : profitState === "pending" ? "wo-profit--pending" : ""}`}>
                    <div>
                        <div className="wo-profit__label">
                            {profitState === "loss" ? "Company loss" : "Company profit"}
                        </div>
                        <div className="wo-profit__hint">
                            {profitState === "pending"
                                ? "Shown after you enter the exchange rate and the buying price of every product"
                                : profitState === "loss"
                                    ? "The company lost this much on the order after paying all commissions"
                                    : "What the company keeps after paying all commissions"}
                        </div>
                    </div>
                    <div>
                        <div className="wo-profit__pkr">{formatMoney(profit?.pkr, "PKR")}</div>
                        <div className="wo-profit__usd">{formatMoney(profit?.usd, "USD")}</div>
                    </div>
                </div>

                {complete && summary.settlement && (() => {
                    const receivable = summary.settlement.direction === "mplacer_pays_company";

                    return (
                        <div className={`wo-settle ${receivable ? "wo-settle--in" : "wo-settle--out"}`}>
                            <div>
                                <span className="wo-settle__tag">{receivable ? "Receivable" : "Payable"}</span>
                                <div className="wo-settle__label">{summary.settlement.text}</div>
                                <div className="wo-settle__hint">{summary.settlement.hint}</div>
                            </div>
                            <div className="text-end">
                                <div className="wo-settle__pkr">{formatMoney(summary.settlement.pkr, "PKR")}</div>
                                <div className="wo-settle__usd">{formatMoney(summary.settlement.usd, "USD")}</div>
                            </div>
                        </div>
                    );
                })()}
            </div>
        </section>
    );
}
